/**
 * Camada 1 · botao.
 *
 * Tres variantes e nada mais. Cada variante nova e uma decisao a menos tomada no design
 * e uma a mais empurrada para quem escreve a tela, e o resultado disso e interface em
 * que dois botoes iguais fazem coisas de peso diferente.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { IconeIa } from './Icones.js';

/**
 * As variantes, e cada uma é um PAPEL na tela, nunca uma aparência.
 *
 * `ia` se chamava `inverso` até 06/09/2026. O tipo é FECHADO de propósito: é ele que faz o
 * typecheck recusar a grafia antiga em todo o repositório de uma vez, em vez de a troca
 * depender de alguém varrer os apps procurando.
 */
export type VarianteBotao = 'primario' | 'secundario' | 'texto' | 'contorno' | 'fantasma' | 'ia';
export type TamanhoBotao = 'md' | 'sm' | 'icone';

export interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variante?: VarianteBotao;
  readonly tamanho?: TamanhoBotao;
  /** Enquanto true, o botao fica desabilitado e anuncia o estado ao leitor de tela.
   *  Sem isso, dois cliques rapidos disparam a mesma acao duas vezes. */
  readonly carregando?: boolean;
  readonly children: ReactNode;
}

// As classes saem do preset do Tailwind, que aponta para os tokens. Nenhuma cor literal
// aparece aqui, e a regra de lint quebra o build se alguem tentar.
const POR_VARIANTE: Record<VarianteBotao, string> = {
  /*
   * ==========================================================================
   * O PRIMARIO EM VIDRO, e isto e PADRAO DA PLATAFORMA desde 09/09/2026
   * ==========================================================================
   * Pedidos dele naquele dia, olhando o Financeiro e depois o Login:
   *   "O AZUL DO BOTAO TB, TEM QUE SER GLASS, TUDO GLASS E SUAVE. PADRAO PARA PLT"
   *   "NO LOGIN TB TEM QUE ARRUMAR O BOTAO PARA GLASS." e "EM AMBOS OS TEMAS."
   * E, fechando o alcance: "TUDO O QUE DEFINIRMOS NESSA SESSAO EM RELACAO A CORES E FORMAS E
   * PADRAO DA PLATAFORMA."
   *
   * O QUE ESTAVA ERRADO: `bg-marca` e cor CHEIA, e no escuro ela vale `56 189 248`, um ciano
   * vivo. O botao "Entrar" do login e uma barra de 350px daquele azul, e o "Registrar
   * pagamento" se repete em cada cartao da lista. Repetido, o fundo cheio deixa de destacar
   * a acao e passa a ser ruido: quando tudo grita, nada e principal.
   *
   * A TENSAO COM O PADRAO E REAL, e vale registrar em vez de esconder: o padrao Apple diz
   * "So a acao principal tem fundo cheio". Ele pediu vidro TAMBEM no primario, e a decisao e
   * dele. O que preserva a hierarquia e a tinta continuar sendo a da MARCA e a borda ser
   * cheia: o primario continua sendo o unico botao com cor propria, e os outros seguem
   * neutros ou transparentes.
   *
   * O TEXTO NAO GANHOU ALPHA. Ele passou de `marca-contraste` (que assenta sobre a cor
   * cheia) para a propria `marca`, porque sobre vidro o fundo e quase o do cartao: texto
   * branco ali sumiria. Este e o mesmo raciocinio do padrao Apple sobre "texto sobre cor
   * cheia usa o contraste declarado, e nunca #fff fixo".
   *
   * CADA TEMA COM O SEU ALPHA, e isso foi aprendido na tela hoje e nao deduzido: tinta clara
   * sobre fundo quase preto ganha contraste, e tinta escura sobre fundo quase branco perde.
   */
  /*
   * `text-marca-forte` E NAO `text-marca`, e o numero decidiu.
   *
   * A varredura de 09/09/2026 mediu a MODA DE PIXEL do botao "Entrar" do login, no tema
   * claro: `text-marca` sobre `bg-marca/15` assentado na pagina da **4.32:1**, abaixo do
   * piso de 4.5. `text-marca-forte` (que ja existia, valendo `7 89 133`) da **5.50:1**.
   *
   * O ACHADO QUE MUDA COMO SE PENSA VIDRO, e ele e maior que este botao: o MESMO botao passa
   * com 4.77:1 quando esta sobre um cartao BRANCO, e falha com 4.32:1 sobre a pagina cinza.
   * Com tinta transparente, o contraste DEIXOU DE SER PROPRIEDADE DO COMPONENTE e passou a
   * ser propriedade do que esta atras dele. Um botao que passa numa tela pode falhar na
   * seguinte sem que nada nele mude.
   *
   * `--cx-marca` NAO foi alterada para consertar isto, e a decisao esta em `tokens.css`: ela
   * e IDENTIDADE, e troca-la seria consertar acessibilidade destruindo o que ela significa.
   * O caminho e o TEXTO usar o token mais escuro que ja existe para isso.
   */
  primario:
    'bg-marca/15 text-marca-forte border border-marca/50 hover:bg-marca/25 ' +
    'dark:bg-marca/20 dark:border-marca/40 dark:hover:bg-marca/30 ' +
    'backdrop-blur-vidro backdrop-saturate-vidro',
  secundario: 'bg-superficie text-texto border border-borda hover:bg-superficie-elevada',
  texto: 'bg-transparent text-marca border border-transparent hover:bg-marca-suave',
  // CONTORNO: a borda e da MARCA, e nao da borda neutra. E o par visual do primario quando
  // duas acoes de mesmo peso dividem a linha e uma delas nao pode dominar. Veio do Diario,
  // onde "exportar" convive com "lancar" sem competir com ele.
  contorno: 'bg-transparent text-marca border border-marca hover:bg-marca-suave',
  // FANTASMA: sem fundo e sem borda ate o ponteiro chegar. E a acao TERCIARIA, aquela que
  // precisa estar alcancavel e nao pode puxar o olho. Veio do Painel e do EscolherCliente,
  // onde "voltar" e "trocar" moram ao lado do conteudo e nao concorrem com ele.
  fantasma: 'bg-transparent text-texto-suave border border-transparent hover:bg-superficie-afundada',
  /*
    IA: o botao que ACIONA uma funcao de inteligencia artificial.

    PADRAO DA PLATAFORMA, decidido por ele em 06/09/2026: *"todos os botoes de IA (que
    acionam funcoes de IA) sao PRETOS no tema claro. Importante registrarmos na esteira da
    plataforma."*

    ELA SE CHAMAVA `inverso` ATE ESTE DIA, e a troca de nome e o trabalho inteiro. A receita
    visual ja existia e ninguem a usava: medido em 06/09/2026, ZERO ocorrencias de
    `variante="inverso"` em todo o repositorio. O motivo e que `inverso` descreve a
    APARENCIA, e quem vai escrever um botao de IA nao procura por "inverso": ele escreve o
    preto a mao, que e exatamente o que o prontidaovoo do legado fazia, com o comentario "O
    PRETO DO LEGADO" ao lado.

    O NOME DIZ O PAPEL, e nao o tom, pela mesma razao que `--marca` nao se chama `--azul`:
    cor muda e papel nao. No dia em que o preto da IA virar outra coisa, muda aqui, e alcanca
    os dez apps de uma vez, que e o que ele pediu em 06/09/2026: *"NO CASO DE MUDAR UM ESTILO
    DE BOTAO PARA 10 APPS QUE TEREI, QUERO FAZER ISSO EM UM PROMTO."*

    🔴 ELA NAO E PRETA, ELA INVERTE COM O TEMA, e a diferenca importa: `bg-texto` resolve
    para o quase-preto no tema claro e para o quase-branco no escuro. Fixar `#000` aqui
    cumpriria a frase dele ao pe da letra e produziria um botao invisivel no tema escuro,
    que e o oposto do que ele quer.

    A ESTEIRA QUE A GARANTE, e ela nao depende de ninguem lembrar: o lint recusa `<button>`
    cru em `apps/**`, entao todo botao passa por esta peca; `VarianteBotao` e um tipo
    FECHADO, entao o typecheck recusa qualquer grafia que nao esteja aqui; e o
    `check-colors.mjs` recusa cor literal, entao ninguem escreve o preto a mao.
  */
  // A RECEITA USA OS TOKENS DE IA, e nao `bg-texto text-fundo`, que era o que ela tinha.
  //
  // O DEFEITO, medido na tela dele em 07/09/2026: o botao saia PRETO NO PRETO, sem rotulo
  // e sem simbolo. A causa e que `fundo` NAO E TOKEN desta plataforma: o Tailwind ignora a
  // classe inexistente EM SILENCIO, sem erro e sem aviso, e o texto herda a cor do pai, que
  // sobre `bg-texto` e preto sobre preto.
  //
  // OS TOKENS DE IA JA EXISTIAM e ninguem os usava: --cx-ia, --cx-ia-contraste,
  // --cx-ia-hover e --cx-ia-borda, declarados nos DOIS temas. E o mesmo padrao que fez a
  // variante nascer chamada `inverso` com zero usos: a receita existia e nao era encontrada.
  //
  // O QUE CADA UM RESOLVE, e por isso sao quatro e nao um: `ia` e o fundo (23 23 23 no
  // claro, 5 5 5 no escuro, e nunca #000 puro, que vibra); `ia-contraste` e o texto, que
  // muda com o tema; `ia-hover` e o estado do ponteiro; e `ia-borda` desenha a quina no
  // tema escuro, onde o botao quase se confunde com a superficie.
  ia: 'bg-ia text-ia-contraste border border-ia-borda hover:bg-ia-hover',
};

const POR_TAMANHO: Record<TamanhoBotao, string> = {
  // min-h-toque garante o alvo de 44px tambem no computador, para o botao nao mudar de
  // altura quando a mesma tela e aberta no celular.
  md: 'min-h-toque px-cx-4 text-cx-sm',
  sm: 'min-h-toque px-cx-3 text-cx-xs',
  // ICONE: quadrado, com o minimo de toque nos DOIS eixos. Sem `min-w-toque` o botao so de
  // icone encolhe ate a largura do desenho, e um alvo de 44px de altura por 20 de largura
  // falha no dedo exatamente como um de 20 por 20. A varredura de Seguranca encontrou um
  // assim, escrito a mao com `min-w-toque` avulso, que e a duplicacao que esta Peca fecha.
  icone: 'min-h-toque min-w-toque shrink-0 text-cx-sm',
};

export function Botao({
  variante = 'primario',
  tamanho = 'md',
  carregando = false,
  disabled,
  className = '',
  children,
  ...resto
}: BotaoProps) {
  /*
   * O SIMBOLO DA IA E DESENHADO PELO PROPRIO BOTAO, e nao por quem o chama.
   *
   * Pedido dele em 07/09/2026: "TODO BOTAO DE ACAO DA IA TEM QUE SER PRETA COM SIMBOLO DA IA
   * DENTRO DO BOTAO TB · ISSO TEM QUE VALER PARA TODA A PLATAFORMA · CRIE O MECANISMO".
   *
   * E A PALAVRA MECANISMO QUE DECIDE ONDE ISTO MORA. Pedir que cada tela escreva o icone ao
   * lado do rotulo seria INSTRUCAO, e instrucao se esquece sem erro nenhum: o botao sairia
   * preto e sem simbolo, e ninguem notaria ate alguem olhar a tela. Desenhado aqui, e
   * IMPOSSIVEL existir botao de IA sem o simbolo, porque a variante e o simbolo passam a ser
   * a mesma decisao.
   *
   * ELE E `aria-hidden` de proposito: o rotulo ja diz o que a acao faz, e um leitor de tela
   * anunciando "imagem" antes de "Gerar nota SOAP" atrapalha em vez de ajudar.
   *
   * NO TAMANHO `icone` ele NAO entra: ali o desenho que a tela passou como `children` E o
   * conteudo do botao, e somar um segundo simbolo desenharia dois icones num alvo de 44px.
   */
  const simboloDaIa = variante === 'ia' && tamanho !== 'icone';

  return (
    <button
      // `type` explicito: sem ele, um botao dentro de formulario e submit por padrao e
      // envia a tela ao ser clicado para outra coisa qualquer.
      //
      // ELE E SOBRESCRIVEL, e isso e proposital: `{...resto}` vem DEPOIS, entao a tela que
      // precisa de `type="submit"` passa a prop e ela vence. O padrao protege o caso comum
      // sem impedir o caso legitimo, que e o botao que ENVIA o formulario em que vive.
      type="button"
      disabled={disabled === true || carregando}
      aria-busy={carregando || undefined}
      className={[
        'inline-flex items-center justify-center gap-cx-2 rounded-cx-md font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        POR_VARIANTE[variante],
        POR_TAMANHO[tamanho],
        className,
      ].join(' ')}
      {...resto}
    >
      {/* ANTES do rotulo: e a ordem de leitura de quem olha, e a mesma dos demais botoes de
          acao da plataforma. Enquanto carrega ele sai, para nao competir com o giro. */}
      {simboloDaIa && !carregando ? <IconeIa className="h-4 w-4" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}
